import * as motion from 'motion/react-m';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconComponent } from '@/components/IconComponent';
import {
  amountWithCurrencySymbol,
  DEFAULT_CURRENCIES,
} from '@/helpers/currency';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import { useUserWageringBonuses } from '@/hooks/bonuses';
import { useGetCommonStore } from '@/store';
import { ScWagerLabel, ScWagerLabelOuter } from './styled';

export const WagerLabel = memo(() => {
  const { t } = useTranslation('common');

  const [isOpen, setIsOpen] = useState(true);

  const fetchBonuses = useGetCommonStore().use.fetchBonuses();

  const { activeWageringBonusByCurrency } = useUserWageringBonuses();

  const wager = useMemo(() => {
    if (!activeWageringBonusByCurrency) return;

    const { amountWagerRequirementCents, amountWagerCents, currency } =
      activeWageringBonusByCurrency;

    const { amount: wagerAmount } = getCurrencyCentsAndCode(
      amountWagerCents,
      currency,
      DEFAULT_CURRENCIES
    );

    const { amount: requiredWagerAmount } = getCurrencyCentsAndCode(
      amountWagerRequirementCents,
      currency,
      DEFAULT_CURRENCIES
    );

    return t('game.wager', {
      currentValue: amountWithCurrencySymbol(currency, wagerAmount, {
        noDecimals: true,
      }),
      wagerValue: amountWithCurrencySymbol(currency, requiredWagerAmount, {
        noDecimals: true,
      }),
    });
  }, [activeWageringBonusByCurrency, t]);

  const handleLabelClick = useCallback(() => {
    setIsOpen((prevIsOpen) => {
      if (!prevIsOpen) fetchBonuses();
      return !prevIsOpen;
    });
  }, [fetchBonuses]);

  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      setIsOpen(false);
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  return (
    <ScWagerLabelOuter>
      <ScWagerLabel onClick={handleLabelClick}>
        <div className="icon">
          <IconComponent name="wager" size="24px" />
        </div>
        <motion.div
          animate={isOpen ? 'open' : 'closed'}
          className="wager-info"
          initial={{ width: 0, opacity: 0, paddingRight: 0 }}
          layout
          transition={{ duration: 0.2 }}
          variants={{
            closed: { width: 0, opacity: 0, paddingRight: 0 },
            open: { width: 'auto', opacity: 1, paddingRight: '12px' },
          }}
        >
          <div className="wager">{t('game.bonusWagering')}</div>
          <div className="amount">{wager}</div>
        </motion.div>
      </ScWagerLabel>
    </ScWagerLabelOuter>
  );
});
